import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ChefHat, Video, Sparkles, BookOpen } from "lucide-react";
import { SmartbouffeLogoLarge } from "../components/Logo";
import { EyeIcon, EyeOffIcon } from "../components/Icons";

export default function Landing() {
  const [showAuth, setShowAuth] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true); // Par défaut activé
  const { setUser, checkAuth } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash;
    if (hash.includes("session_id=")) {
      const sessionId = hash.split("session_id=")[1].split("&")[0];
      handleGoogleCallback(sessionId);
    }
  }, [location]);

  const handleGoogleCallback = async (sessionId) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/google`,
        { session_id: sessionId },
        { withCredentials: true }
      );
      setUser(response.data.user);
      window.history.replaceState({}, document.title, "/dashboard");
      navigate("/dashboard");
      toast.success("Connexion réussie!");
    } catch (error) {
      toast.error("Échec de la connexion Google");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = `${window.location.origin}/dashboard`;
    const authUrl = process.env.REACT_APP_AUTH_URL;
    if (!authUrl) {
      return;
    }
    window.location.href = `${authUrl}/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData(e.target);
    try {
      const response = await axios.post(
        `${API}/auth/register`,
        {
          email: formData.get("email"),
          password: formData.get("password"),
          name: formData.get("name"),
        },
        { withCredentials: true }
      );
      setUser(response.data.user);
      navigate("/dashboard");
      toast.success("Compte créé avec succès!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData(e.target);
    try {
      const response = await axios.post(
        `${API}/auth/login`,
        {
          email: formData.get("email"),
          password: formData.get("password"),
          remember_me: rememberMe,
        },
        { withCredentials: true }
      );
      
      // Si "Se souvenir de moi" est activé, stocker les credentials localement
      if (rememberMe) {
        localStorage.setItem('smartbouffe_remember', 'true');
        localStorage.setItem('smartbouffe_email', formData.get("email"));
      } else {
        localStorage.removeItem('smartbouffe_remember');
        localStorage.removeItem('smartbouffe_email');
      }
      
      setUser(response.data.user);
      navigate("/dashboard");
      toast.success("Connexion réussie!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la connexion");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setSendingReset(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success("Un code à 6 chiffres a été envoyé à votre email");
      setShowForgotPassword(false);
      // Rediriger vers la page de réinitialisation avec l'email pré-rempli
      navigate(`/reset-password?email=${encodeURIComponent(forgotEmail)}`);
      setForgotEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSendingReset(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen" data-testid="auth-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="landing-page" data-testid="landing-page">
      <div className="landing-header">
        <SmartbouffeLogoLarge className="landing-logo" />
      </div>
      
      <div className="hero-section">
        <div className="hero-content">
          <div className="hero-badge" data-testid="hero-badge">
            <Sparkles size={16} />
            <span>Transformez vos vidéos en recettes</span>
          </div>
          <h1 className="hero-title" data-testid="hero-title">
            Votre Livre de Recettes Virtuel
          </h1>
          <p className="hero-subtitle" data-testid="hero-subtitle">
            Collez simplement un lien vidéo ou une photo/capture d'écran, et notre IA transforme automatiquement le contenu en fiche recette structurée. 
            Fonctionne pour les recettes de cuisine, mais aussi pour les <strong>cocktails, mocktails, sangrias</strong> et toutes vos boissons préférées!
          </p>
          <Button 
            className="cta-button" 
            size="lg" 
            onClick={() => setShowAuth(true)}
            data-testid="get-started-btn"
          >
            <ChefHat className="mr-2" size={20} />
            Commencer gratuitement
          </Button>
        </div>
        
        <div className="features-grid">
          <div className="feature-card" data-testid="feature-card-1">
            <div className="feature-icon">
              <Video size={24} />
            </div>
            <h3>Vidéos & Photos</h3>
            <p>YouTube, TikTok, Facebook + captures d'écran de recettes</p>
          </div>
          
          <div className="feature-card" data-testid="feature-card-2">
            <div className="feature-icon">
              <Sparkles size={24} />
            </div>
            <h3>IA Avancée</h3>
            <p>Transcription et extraction automatique des recettes</p>
          </div>
          
          <div className="feature-card" data-testid="feature-card-3">
            <div className="feature-icon">
              <BookOpen size={24} />
            </div>
            <h3>Gestion complète</h3>
            <p>Organisez, modifiez et recherchez vos recettes facilement</p>
          </div>
        </div>
      </div>

      <footer className="landing-footer">
        <p className="footer-text">Fait avec passion au Québec</p>
      </footer>

      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="auth-dialog" data-testid="auth-dialog">
          <DialogHeader>
            <DialogTitle>Bienvenue</DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="login" className="auth-tabs">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login" data-testid="login-tab">Connexion</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Inscription</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login" data-testid="login-form">
              <form onSubmit={handleLogin} className="auth-form">
                <div className="form-group">
                  <Label htmlFor="login-email">Email</Label>
                  <Input id="login-email" name="email" type="email" required data-testid="login-email-input" />
                </div>
                <div className="form-group">
                  <Label htmlFor="login-password">Mot de passe</Label>
                  <div style={{ position: 'relative' }}>
                    <Input 
                      id="login-password" 
                      name="password" 
                      type={showLoginPassword ? "text" : "password"} 
                      required 
                      data-testid="login-password-input"
                      style={{ paddingRight: '40px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      data-testid="login-toggle-password"
                    >
                      {showLoginPassword ? (
                        <EyeOffIcon size={20} color="#8B7355" />
                      ) : (
                        <EyeIcon size={20} color="#8B7355" />
                      )}
                    </button>
                  </div>
                  <div style={{ textAlign: 'right', marginTop: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAuth(false);
                        setShowForgotPassword(true);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#87A96B',
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        padding: 0
                      }}
                    >
                      Mot de passe oublié ?
                    </button>
                  </div>
                </div>
                
                {/* Case à cocher "Se souvenir de moi" */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem',
                  marginBottom: '1rem'
                }}>
                  <Checkbox 
                    id="remember-me" 
                    checked={rememberMe}
                    onCheckedChange={setRememberMe}
                    data-testid="remember-me-checkbox"
                  />
                  <Label 
                    htmlFor="remember-me" 
                    style={{ 
                      fontSize: '0.9rem', 
                      cursor: 'pointer',
                      color: '#5D6F61'
                    }}
                  >
                    Se souvenir de moi
                  </Label>
                </div>
                
                <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
                  Se connecter
                </Button>
              </form>
              
              <div className="divider">ou</div>
              
              <Button variant="outline" className="w-full" onClick={handleGoogleLogin} data-testid="google-login-btn">
                <svg className="mr-2" width="18" height="18" viewBox="0 0 18 18">
                  <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                  <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707 0-.593.102-1.17.282-1.709V4.959H.957C.347 6.173 0 7.548 0 9c0 1.452.348 2.827.957 4.041l3.007-2.334z"/>
                  <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.959L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
                </svg>
                Continuer avec Google
              </Button>
            </TabsContent>
            
            <TabsContent value="register" data-testid="register-form">
              <form onSubmit={handleRegister} className="auth-form">
                <div className="form-group">
                  <Label htmlFor="register-name">Nom (optionnel)</Label>
                  <Input id="register-name" name="name" type="text" data-testid="register-name-input" />
                </div>
                <div className="form-group">
                  <Label htmlFor="register-email">Email</Label>
                  <Input id="register-email" name="email" type="email" required data-testid="register-email-input" />
                </div>
                <div className="form-group">
                  <Label htmlFor="register-password">Mot de passe</Label>
                  <div style={{ position: 'relative' }}>
                    <Input 
                      id="register-password" 
                      name="password" 
                      type={showRegisterPassword ? "text" : "password"} 
                      required 
                      minLength={6} 
                      data-testid="register-password-input"
                      style={{ paddingRight: '40px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      data-testid="register-toggle-password"
                    >
                      {showRegisterPassword ? (
                        <EyeOffIcon size={20} color="#8B7355" />
                      ) : (
                        <EyeIcon size={20} color="#8B7355" />
                      )}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-btn">
                  S'inscrire
                </Button>
              </form>
              
              <div className="divider">ou</div>
              
              <Button variant="outline" className="w-full" onClick={handleGoogleLogin} data-testid="google-register-btn">
                <svg className="mr-2" width="18" height="18" viewBox="0 0 18 18">
                  <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                  <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707 0-.593.102-1.17.282-1.709V4.959H.957C.347 6.173 0 7.548 0 9c0 1.452.348 2.827.957 4.041l3.007-2.334z"/>
                  <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.959L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
                </svg>
                Continuer avec Google
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Dialog Mot de passe oublié */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontSize: '1.5rem', color: '#5D6F61', textAlign: 'center' }}>
              Mot de passe oublié ?
            </DialogTitle>
          </DialogHeader>
          
          <div style={{ padding: '1rem 0' }}>
            <p style={{ color: '#5D6F61', marginBottom: '1.5rem', textAlign: 'center', fontSize: '0.9rem' }}>
              Entrez votre adresse email et nous vous enverrons un code à 6 chiffres pour réinitialiser votre mot de passe.
            </p>
            
            <form onSubmit={handleForgotPassword}>
              <div className="form-group">
                <Label htmlFor="forgot-email">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="votre@email.com"
                  required
                  disabled={sendingReset}
                />
              </div>
              
              <Button
                type="submit"
                className="w-full"
                disabled={sendingReset}
                style={{ marginTop: '1rem' }}
              >
                {sendingReset ? "Envoi en cours..." : "Envoyer le code"}
              </Button>
            </form>
            
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setShowAuth(true);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#87A96B',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Retour à la connexion
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}